import type { ApiError } from './errors';

/**
 * The single door to the API.
 *
 * Every call carries the active org, because the server verifies membership on
 * every request rather than trusting a session to imply a tenant. A failure
 * arrives as ApiFailure with the server's machine-readable code and field map,
 * so forms can attach messages to inputs without parsing prose.
 */

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly fields?: Record<string, string>,
  ) {
    super(code);
    this.name = 'ApiFailure';
  }
}

let activeOrgId: string | null = null;

export function setActiveOrg(orgId: string | null) {
  activeOrgId = orgId;
}

export function getActiveOrg() {
  return activeOrgId;
}

/**
 * What to do when the server says the session is gone.
 *
 * A session expires or is revoked between one screen and the next, and without
 * this every panel on the page fills with an error while the shell goes on
 * showing a signed-in app. The coach is left looking at a broken screen with
 * nothing telling them to sign in again.
 *
 * Only for calls outside `/auth`: a 401 from sign-in means the password was
 * wrong and belongs in the form, and `/auth/me` answers 401 for every signed
 * out visitor by design.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (activeOrgId) headers['X-Org-Id'] = activeOrgId;

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (payload ?? {}) as ApiError;
    if (res.status === 401 && !path.startsWith('/auth/')) onUnauthorized?.();
    throw new ApiFailure(res.status, err.error ?? 'request_failed', err.fields);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => call<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => call<T>('DELETE', path),
};
